import { useEffect, useState, type ReactNode } from 'react';
import type { MiPerfil, OperacionesClienteFinal } from '../api/ops/clienteFinal';
import type { HuecoCliente, MiCita, ResultadoHuecosCliente, VistaPreviaMiCambio } from '../api/ops/citasCliente';
import { claveIdempotencia } from '../api/comun';
import { ErrorApi } from '../api/problema';
import { Chip } from '../componentes/Chip';
import { Filtros } from '../componentes/Filtros';
import { Icono } from '../componentes/Icono';
import { Paginacion } from '../componentes/Paginacion';
import { dinero } from '../logica/dinero';
import { navegador } from '../logica/navegador';
import { baldosaDia, chipsDeCita, diaCorto, diaLargo, duracion, hoyLocal, mayuscula, minutosDe, nombreServicios, profesionalDe, RUTA_PEDIR_CITA, textoSinHueco, viva } from './citas';
import { useCancelacion } from './cancelacion';
import { DetalleCita, VentanaCancelar, type Banda } from './CitaCliente';
import { ElegirHora } from './ElegirHora';
import { textoDeError } from './logica';
import { AvisoPc, BotonPc, Pagina } from './Pagina';
import './citas.css';

/**
 * «Mis citas» (9.10, `/mis-citas`): con el token de cliente de «Mis datos», el negocio a elegir entre sus perfiles, las
 * próximas («Tu próxima cita» grande arriba y «Más adelante») y las pasadas (`listMyAppointments`, por páginas). Una cita
 * (`getMyAppointment`) se cambia gratis dentro del plazo (`previewMyAppointmentModification` y `modifyMyAppointment`, G434;
 * fuera de plazo, `409 change_window_closed`) o se cancela con el importe a la vista. Al cambiar: «Te mandamos la
 * confirmación con tu pase nuevo» (G480); una pendiente de pago se mueve sin alargar su plazo (G489, G503).
 *
 * `?negocio={business_id}&cita={id}&vista=cambiar` abre esa cita (el «Cambiar la cita» del enlace del mensaje).
 */
interface Props {
    ops: OperacionesClienteFinal;
    busqueda: URLSearchParams;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    onSalir: () => void;
    onCaducada: () => void;
    ahora?: () => number;
}

type Cuando = 'upcoming' | 'past';
type Vista = { tipo: 'lista' } | { tipo: 'cita'; id: string; cambiar: boolean };

export function MisCitas({ ops, busqueda, navegar, onSalir, onCaducada, ahora = Date.now }: Props) {
    const [perfiles, setPerfiles] = useState<MiPerfil[] | null>(null);
    const [elegido, setElegido] = useState<string | null>(null);
    const [cuando, setCuando] = useState<Cuando>('upcoming');
    const [cursores, setCursores] = useState<(string | null)[]>([null]);
    const [pagina, setPagina] = useState<{ clave: string; lista: MiCita[]; siguiente: string | null } | null>(null);
    const [error, setError] = useState(false);
    const [intento, setIntento] = useState(0);
    const [vista, setVista] = useState<Vista>(() => {
        const id = busqueda.get('cita');
        return id === null ? { tipo: 'lista' } : { tipo: 'cita', id, cambiar: busqueda.get('vista') === 'cambiar' };
    });
    const [cita, setCita] = useState<MiCita | null>(null);
    const [banda, setBanda] = useState<Banda | null>(null);

    const tratar = (e: unknown): boolean => {
        if (e instanceof ErrorApi && e.estado === 401) {
            onCaducada();
            return true;
        }
        return false;
    };

    useEffect(() => {
        let vivo = true;
        ops.listMyBusinessProfiles({ limit: 100 })
            .then((l) => {
                if (!vivo) return;
                const activos = l.data.filter((p) => p.ended == null);
                setPerfiles(activos);
                const delEnlace = busqueda.get('negocio');
                const p = activos.find((x) => x.business.id === delEnlace);
                if (p !== undefined) setElegido(p.customer_id);
            })
            .catch((e: unknown) => { if (vivo && !tratar(e)) setError(true); });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, intento]);

    const perfil = perfiles?.find((p) => p.customer_id === elegido) ?? perfiles?.[0] ?? null;
    const idPerfil = perfil?.customer_id ?? null;
    const cursor = cursores[cursores.length - 1] ?? null;
    const clave = `${idPerfil ?? ''}|${cuando}|${cursor ?? ''}`;

    useEffect(() => {
        if (idPerfil === null || vista.tipo !== 'lista') return;
        let vivo = true;
        ops.listMyAppointments(idPerfil, cuando, { cursor, limit: 20 })
            .then((l) => { if (vivo) setPagina({ clave, lista: l.data, siguiente: l.next_cursor }); })
            .catch((e: unknown) => { if (vivo && !tratar(e)) setError(true); });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, clave, vista.tipo, intento]);

    const idCita = vista.tipo === 'cita' ? vista.id : null;
    useEffect(() => {
        if (idPerfil === null || idCita === null) return;
        let vivo = true;
        ops.getMyAppointment(idPerfil, idCita)
            .then((c) => { if (vivo) setCita(c); })
            .catch((e: unknown) => { if (vivo && !tratar(e)) setError(true); });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, idPerfil, idCita, intento]);

    const cancelacion = useCancelacion({
        leer: () => ops.getMyAppointmentCancellationPreview(idPerfil ?? '', idCita ?? ''),
        cancelar: (c) => ops.cancelMyAppointment(idPerfil ?? '', idCita ?? '', c),
        onHecha: (c, v) => {
            setCita(c);
            setBanda({ titulo: 'Cita cancelada', texto: v.refund.amount > 0 ? `Te devolvemos ${dinero(v.refund)}. Puede tardar unos días en verse en tu cuenta.` : 'La hora ha quedado libre. Te mandamos la confirmación.' });
        },
        tratar,
    });

    const nombre = perfil?.business.name ?? '';
    const marco = (contenido: ReactNode, extra: { volver?: { texto: string; accion: () => void }; titulo?: { titulo: string; sub: string }; centrada?: boolean } = {}) => (
        <Pagina cabecera={perfil === null ? { tipo: 'cuenta' } : { tipo: 'negocio', nombre, linea: 'Mis citas' }} pie={{ tipo: 'reservaria' }} onSalir={onSalir} {...extra}>
            {contenido}
        </Pagina>
    );
    const aLaLista = () => {
        setVista({ tipo: 'lista' });
        setBanda(null);
        setCita(null);
    };
    const abrir = (id: string, cambiar = false) => {
        setVista({ tipo: 'cita', id, cambiar });
        setBanda(null);
    };
    const pedirCita = perfil?.business.slug !== undefined ? () => { navegar(`${RUTA_PEDIR_CITA}/${encodeURIComponent(perfil.business.slug ?? '')}`); } : null;

    if (error) {
        return marco(
            <section className="rv-pc-card">
                <div className="rv-pc-card__cab"><h1>No hemos podido cargar tus citas</h1><span>Puede ser la conexión. No se ha perdido nada. Prueba de nuevo.</span></div>
                <BotonPc texto="Reintentar" icono="refresh-cw" onClick={() => { setError(false); setIntento((i) => i + 1); }} />
            </section>,
            { centrada: true },
        );
    }

    // ---- Una cita ----
    if (vista.tipo === 'cita') {
        if (cita === null || cita.id !== vista.id) {
            return marco(<section className="rv-pc-card" aria-busy="true" aria-label="Cargando"><div className="rv-pc-esqueleto" style={{ height: 160 }} /></section>, { volver: { texto: 'Mis citas', accion: aLaLista } });
        }
        if (vista.cambiar && viva(cita) && cita.can_modify && idPerfil !== null) {
            return marco(
                <CambiarCita
                    ops={ops}
                    perfil={idPerfil}
                    negocioId={perfil?.business.id ?? ''}
                    cita={cita}
                    negocio={nombre}
                    ahora={ahora}
                    tratar={tratar}
                    onCambiada={(c, b) => {
                        setCita(c);
                        setBanda(b);
                        setVista({ tipo: 'cita', id: c.id, cambiar: false });
                    }}
                />,
                { volver: { texto: 'Tu cita', accion: () => { setVista({ tipo: 'cita', id: cita.id, cambiar: false }); } } },
            );
        }
        return marco(
            <>
                <DetalleCita
                    cita={cita}
                    porEnlace={false}
                    negocio={nombre}
                    banda={banda}
                    onCambiar={() => { abrir(cita.id, true); }}
                    onCancelar={cancelacion.abrir}
                    onFirmar={() => { navegar(`/mis-documentos?negocio=${encodeURIComponent(perfil?.business.id ?? '')}`); }}
                    onPedirOtra={pedirCita}
                />
                <VentanaCancelar
                    abierta={cancelacion.abierta}
                    cita={cita}
                    vista={cancelacion.vista}
                    cambiada={cancelacion.cambiada}
                    error={cancelacion.error}
                    nota={cancelacion.nota}
                    onNota={cancelacion.setNota}
                    ocupado={cancelacion.ocupado}
                    onCancelar={cancelacion.confirmar}
                    onCerrar={cancelacion.cerrar}
                />
            </>,
            { volver: { texto: 'Mis citas', accion: aLaLista } },
        );
    }

    // ---- La lista ----
    const lista = pagina !== null && pagina.clave === clave ? pagina.lista : null;
    const primera = cuando === 'upcoming' && cursores.length === 1 && lista !== null && lista.length > 0 ? (lista[0] ?? null) : null;
    const resto = lista === null ? [] : primera === null ? lista : lista.slice(1);
    const hoy = hoyLocal(new Date(ahora()));
    const manana = (() => {
        const d = new Date(ahora());
        d.setDate(d.getDate() + 1);
        return hoyLocal(d);
    })();
    const cambiarCuando = (c: Cuando) => {
        setCuando(c);
        setCursores([null]);
    };

    return marco(
        <>
            {perfiles !== null && perfiles.length > 1 && (
                <label className="rv-pc-negocio__canal">
                    <span>Negocio</span>
                    <select className="rv-pc-select rv-pc-select--ancho" value={idPerfil ?? ''} onChange={(e) => { setElegido(e.target.value); setCursores([null]); }}>
                        {perfiles.map((p) => <option key={p.customer_id} value={p.customer_id}>{p.business.name}</option>)}
                    </select>
                </label>
            )}
            <Filtros etiqueta="Qué citas ver" items={[{ etiqueta: 'Próximas' }, { etiqueta: 'Pasadas' }]} valor={cuando === 'upcoming' ? 0 : 1} onCambio={(i) => { cambiarCuando(i === 0 ? 'upcoming' : 'past'); }} />
            {perfiles !== null && perfil === null ? (
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h2>Todavía no tienes citas</h2><span>Cuando pidas una, la verás aquí y podrás cambiarla o cancelarla.</span></div>
                </section>
            ) : lista === null ? (
                <section className="rv-pc-card" aria-busy="true" aria-label="Cargando">
                    <div className="rv-pc-esqueleto" style={{ height: 56 }} />
                    <div className="rv-pc-esqueleto" style={{ height: 56 }} />
                </section>
            ) : lista.length === 0 ? (
                <section className="rv-pc-card">
                    <div className="rv-pc-vacio">
                        <span aria-hidden="true"><Icono nombre="inbox" /></span>
                        <strong>{cuando === 'upcoming' ? `No tienes citas próximas en ${nombre}` : `Todavía no tienes citas pasadas en ${nombre}`}</strong>
                        <small>{cuando === 'upcoming' ? 'Cuando pidas una, la verás aquí y podrás cambiarla o cancelarla.' : 'Aquí verás las citas que ya pasaron, también las canceladas.'}</small>
                    </div>
                    {cuando === 'upcoming' && pedirCita !== null && <BotonPc texto="Pedir cita" icono="calendar-plus" onClick={pedirCita} />}
                </section>
            ) : (
                <>
                    {primera !== null && (
                        <section className="rv-pc-card" aria-label="Tu próxima cita">
                            <span className="rv-pc-ceja">Tu próxima cita</span>
                            <div className="rv-pc-card__cab">
                                <h2 className="rv-ct-grande">{`${primera.local_date === hoy ? 'Hoy' : primera.local_date === manana ? 'Mañana' : mayuscula(diaCorto(primera.local_date))} a las ${primera.local_time}`}</h2>
                                <span>{`${nombreServicios(primera)}${profesionalDe(primera) === null ? '' : ` · con ${profesionalDe(primera) ?? ''}`}`}</span>
                                <span>{[primera.local_date === hoy || primera.local_date === manana ? mayuscula(diaLargo(primera.local_date)) : '', duracion(minutosDe(primera))].filter((x) => x !== '').join(' · ')}</span>
                                {chipsDeCita(primera)[0] !== undefined && <span style={{ alignSelf: 'flex-start' }}><Chip estado={chipsDeCita(primera)[0]?.chip ?? 'neutral'}>{chipsDeCita(primera)[0]?.texto}</Chip></span>}
                            </div>
                            {primera.consent_status === 'requested' && viva(primera) ? (
                                <BotonPc texto="Firmar el consentimiento" icono="signature" onClick={() => { navegar(`/mis-documentos?negocio=${encodeURIComponent(perfil?.business.id ?? '')}`); }} />
                            ) : (
                                <BotonPc texto="Ver la cita" icono="calendar" onClick={() => { abrir(primera.id); }} />
                            )}
                        </section>
                    )}
                    {primera !== null && resto.length > 0 && <h2 className="rv-final-osc rv-ct-titulo-lista">Más adelante</h2>}
                    {resto.length > 0 && (
                        <section className="rv-pc-card" aria-label={cuando === 'upcoming' ? 'Citas próximas' : 'Citas pasadas'} style={{ gap: 0 }}>
                            {resto.map((c) => {
                                const b = baldosaDia(c.local_date);
                                const pro = profesionalDe(c);
                                return (
                                    <button key={c.id} type="button" className={`rv-ct-fila${viva(c) || c.status === 'completed' ? '' : ' rv-ct-fila--apagada'}`} onClick={() => { abrir(c.id); }}>
                                        <span aria-hidden="true" className="rv-ct-dia"><small>{b.semana}</small><strong>{b.dia}</strong><small>{b.mes}</small></span>
                                        <span>
                                            <strong>{nombreServicios(c)}</strong>
                                            <small>{`${c.local_time}${pro === null ? '' : ` · con ${pro}`} · ${duracion(minutosDe(c))}`}</small>
                                            {chipsDeCita(c).length > 0 && <span className="rv-ct-chips">{chipsDeCita(c).map((x) => <Chip key={x.texto} estado={x.chip}>{x.texto}</Chip>)}</span>}
                                        </span>
                                        <Icono nombre="chevron-right" />
                                    </button>
                                );
                            })}
                        </section>
                    )}
                    {(cursores.length > 1 || pagina?.siguiente != null) && (
                        <Paginacion
                            hayAnterior={cursores.length > 1}
                            haySiguiente={pagina?.siguiente != null}
                            onAnterior={() => { setCursores(cursores.slice(0, -1)); }}
                            onSiguiente={() => { if (pagina?.siguiente != null) setCursores([...cursores, pagina.siguiente]); }}
                        />
                    )}
                </>
            )}
        </>,
        { titulo: { titulo: 'Mis citas', sub: nombre === '' ? 'Tus citas: cámbialas o cancélalas desde aquí.' : `Tus citas en ${nombre}: cámbialas o cancélalas desde aquí.` } },
    );
}

/**
 * Cambiar la cita (9.10). Los huecos del día los da la API (`searchAppointmentSlots` con el token del cliente, G866): con
 * los servicios, las respuestas y el profesional de la cita (con «cualquiera», sin profesional) y `exclude_appointment_id`,
 * para que su propio hueco no cuente. Al elegir uno, la vista previa (`previewMyAppointmentModification`) dice lo que pasa
 * con el dinero, y se cambia con `modifyMyAppointment`. Se mueve todo junto.
 */
function CambiarCita({ ops, perfil, negocioId, cita, negocio, ahora, tratar, onCambiada }: { ops: OperacionesClienteFinal; perfil: string; negocioId: string; cita: MiCita; negocio: string; ahora: () => number; tratar: (e: unknown) => boolean; onCambiada: (c: MiCita, b: Banda) => void }) {
    const [dia, setDia] = useState(cita.local_date);
    const [huecos, setHuecos] = useState<{ dia: string; r: ResultadoHuecosCliente | null } | null>(null);
    const [intentoHuecos, setIntentoHuecos] = useState(0);
    const [elegido, setElegido] = useState<HuecoCliente | null>(null);
    const [previa, setPrevia] = useState<{ hueco: HuecoCliente; v: VistaPreviaMiCambio } | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [aviso, setAviso] = useState<string | null>(null);
    const [cerrado, setCerrado] = useState(false);
    const [idem] = useState(claveIdempotencia);
    const concreto = cita.professional_choice === 'specific' ? (cita.services.find((s) => s.professional_id != null)?.professional_id ?? null) : null;

    useEffect(() => {
        let vivo = true;
        ops.searchMyAppointmentSlots(negocioId, {
            services: cita.services.flatMap((s) => (s.service_id === undefined ? [] : [{ service_id: s.service_id, ...(s.answers !== undefined && s.answers.length > 0 ? { answers: s.answers } : {}) }])),
            professional_id: concreto,
            mode: 'list',
            from: dia,
            to: dia,
            exclude_appointment_id: cita.id,
            ...(cita.location_id == null ? {} : { location_id: cita.location_id }),
        })
            .then((r) => { if (vivo) setHuecos({ dia, r }); })
            .catch((e: unknown) => { if (vivo && !tratar(e)) setHuecos({ dia, r: null }); });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocioId, cita.id, dia, intentoHuecos]);

    const cambio = (h: HuecoCliente) => ({ local_date: h.local_date, local_time: h.local_time, ...(concreto === null && h.professional_id != null ? { professional_id: h.professional_id } : {}) });
    const elegir = (h: HuecoCliente) => {
        setDia(h.local_date);
        setElegido(h);
        setAviso(null);
        setPrevia(null);
        ops.previewMyAppointmentModification(perfil, cita.id, cambio(h))
            .then((v) => { setPrevia({ hueco: h, v }); })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'change_window_closed') setCerrado(true);
                else if (!tratar(e)) setAviso(textoDeError(e));
            });
    };
    const cambiar = () => {
        if (previa === null || ocupado) return;
        setOcupado(true);
        setAviso(null);
        const vuelta = (() => {
            try {
                const u = new URL(window.location.href);
                return u.protocol === 'https:' ? u.toString() : null;
            } catch {
                return null;
            }
        })();
        ops.modifyMyAppointment(perfil, cita.id, { ...cambio(previa.hueco), ...(vuelta === null ? {} : { return_url: vuelta }) }, idem)
            .then((c) => {
                if (c.payment_action != null) {
                    navegador.ir(c.payment_action.url);
                    return;
                }
                onCambiada(c, { titulo: 'Cita cambiada', texto: `Ahora es el ${diaLargo(c.local_date)} a las ${c.local_time}. Te mandamos la confirmación con tu pase nuevo: el de antes ya no vale.` });
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'change_window_closed') setCerrado(true);
                else if (e instanceof ErrorApi && (e.codigo === 'capacity_unavailable' || e.codigo === 'hold_expired')) {
                    setPrevia(null);
                    setElegido(null);
                    setIntentoHuecos((i) => i + 1);
                    setAviso('Alguien acaba de coger esa hora. Tu cita sigue como estaba: elige otra.');
                } else if (!tratar(e)) setAviso(textoDeError(e));
            })
            .finally(() => { setOcupado(false); });
    };

    if (cerrado) {
        return (
            <section className="rv-pc-card">
                <div className="rv-pc-card__cab"><h1>Ya no se puede cambiar desde aquí</h1><span>{`Ha pasado el plazo para cambiarla gratis. Para cambiarla, llama a ${negocio}.`}</span></div>
            </section>
        );
    }

    const v = previa?.v ?? null;
    const pro = profesionalDe(cita);
    const proNuevo = previa?.hueco.professional_display_name ?? pro;
    const efecto =
        v === null || !v.available
            ? null
            : v.payment_effect === 'charge' && v.difference !== undefined
              ? { titulo: 'Hay que pagar la diferencia', texto: `Se cobra ${dinero({ amount: Math.abs(v.difference.amount), currency: v.difference.currency })}. Te llevamos a pagarla al confirmar.` }
              : v.payment_effect === 'refund' && v.difference !== undefined
                ? { titulo: 'Te devolvemos la diferencia', texto: `Te devolvemos ${dinero({ amount: Math.abs(v.difference.amount), currency: v.difference.currency })}.` }
                : {
                      titulo: 'Sin coste',
                      texto: cita.status === 'pending_payment' ? 'El cambio es gratis. Cambiar la hora no alarga el plazo para pagar la señal.' : 'Estás dentro del plazo: el cambio es gratis y la señal que pagaste vale para la nueva hora.',
                  };
    const resultado = huecos?.dia === dia ? huecos.r : null;
    const sin = textoSinHueco(v !== null && !v.available ? v.unavailable_reason : resultado?.unavailable_reason, dia, negocio, minutosDe(cita), pro);

    return (
        <>
            <section className="rv-pc-card" aria-labelledby="rv-ct-cambiar-t">
                <div className="rv-pc-card__cab">
                    <h1 id="rv-ct-cambiar-t">Elige la nueva hora</h1>
                    <span>{`Ahora: ${diaLargo(cita.local_date)}, ${cita.local_time}. Se mueve todo junto: ${cita.services.map((s) => s.name.toLowerCase()).join(' y ')}.`}</span>
                </div>
                <ElegirHora
                    profesionales={[]}
                    profesional={concreto}
                    onProfesional={() => undefined}
                    dia={dia}
                    minDia={hoyLocal(new Date(ahora()))}
                    onDia={(d) => { setDia(d); setElegido(null); setPrevia(null); }}
                    resultado={huecos === null || huecos.dia !== dia ? null : huecos.r}
                    error={huecos !== null && huecos.dia === dia && huecos.r === null}
                    onReintentar={() => { setIntentoHuecos((i) => i + 1); }}
                    elegido={elegido === null ? null : elegido.starts_at}
                    onElegir={elegir}
                    sinHueco={sin}
                    zona={cita.timezone ?? ''}
                />
            </section>
            {previa !== null && efecto !== null && (
                <section className="rv-pc-card" aria-live="polite" aria-labelledby="rv-ct-asi-t">
                    <h2 id="rv-ct-asi-t" style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Así queda tu cita</h2>
                    <div className="rv-ct-antes-despues">
                        <span>Ahora</span><span>{`${mayuscula(diaCorto(cita.local_date))}, ${cita.local_time}${pro === null ? '' : ` · con ${pro}`}`}</span>
                        <span>Nueva</span><span>{`${mayuscula(diaCorto(previa.hueco.local_date))}, ${previa.hueco.local_time}${proNuevo === null ? '' : ` · con ${proNuevo}`}`}</span>
                    </div>
                    <div className="rv-ct-aviso"><Icono nombre="circle-check" /><span><b>{efecto.titulo}</b><span>{efecto.texto}</span></span></div>
                    <BotonPc texto="Cambiar mi cita" icono="check" ocupado={ocupado} ocupadoTexto="Cambiando…" onClick={cambiar} />
                </section>
            )}
            {v !== null && !v.available && <AvisoPc texto={`${sin.titulo}. ${sin.texto}`} />}
            {aviso !== null && <AvisoPc texto={aviso} />}
        </>
    );
}
